/**
 * Página: WebhookRawIngestMonitor
 * 
 * Dashboard de auditoria para visualizar todos os webhooks recebidos (raw ingest).
 * Permite filtrar por provider, status, telefone, evento, instance e período.
 * Utiliza RPC server-side para consultas seguras e eficientes.
 */

import { useState, useMemo } from 'react';
import { RefreshCw, Database, Clock, ChevronDown, ChevronRight, Search } from 'lucide-react';
import { format } from 'date-fns';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { useWebhookRawIngest, type WebhookRawIngestFilters, type WebhookRawIngestRecord } from '@/hooks/useWebhookRawIngest';
import { CalendarIcon } from 'lucide-react';

/** Mapeamento de cores por status */
const statusBadgeVariant: Record<string, { className: string; label: string }> = {
  processed: { className: 'bg-green-500/15 text-green-700 border-green-500/30', label: 'Processed' },
  discarded: { className: 'bg-yellow-500/15 text-yellow-700 border-yellow-500/30', label: 'Discarded' },
  error: { className: 'bg-red-500/15 text-red-700 border-red-500/30', label: 'Error' },
  pending: { className: 'bg-muted text-muted-foreground border-border', label: 'Pending' },
};

/** Cores por provider */
const providerColors: Record<string, string> = {
  'z-api': 'bg-green-500/15 text-green-700 border-green-500/30',
  'waha': 'bg-blue-500/15 text-blue-700 border-blue-500/30',
};

export default function WebhookRawIngestMonitor() {
  // Estado dos filtros
  const [filters, setFilters] = useState<WebhookRawIngestFilters>({
    limit: 100,
  });
  const [startDate, setStartDate] = useState<Date | undefined>();
  const [endDate, setEndDate] = useState<Date | undefined>();
  const [phoneInput, setPhoneInput] = useState('');
  const [eventInput, setEventInput] = useState('');
  const [instanceInput, setInstanceInput] = useState('');

  // Linha expandida para ver payload
  const [expandedId, setExpandedId] = useState<string | null>(null);

  // Query via RPC
  const { data: records, isLoading, refetch, isFetching } = useWebhookRawIngest(filters);

  /** Contadores de resumo baseados nos resultados */
  const summary = useMemo(() => {
    if (!records) return { total: 0, processed: 0, discarded: 0, error: 0, pending: 0 };
    return {
      total: records.length,
      processed: records.filter(r => r.resolution_status === 'processed').length,
      discarded: records.filter(r => r.resolution_status === 'discarded').length,
      error: records.filter(r => r.resolution_status === 'error').length,
      pending: records.filter(r => r.resolution_status === 'pending').length,
    };
  }, [records]);

  /** Aplicar filtros de texto (busca ao pressionar Enter ou botão) */
  const handleApplyFilters = () => {
    setFilters(prev => ({
      ...prev,
      phone: phoneInput || undefined,
      event_type: eventInput || undefined,
      instance_id: instanceInput || undefined,
      start_date: startDate ? startDate.toISOString() : undefined,
      end_date: endDate ? endDate.toISOString() : undefined,
    }));
  };

  /** Limpar todos os filtros */
  const handleClearFilters = () => {
    setFilters({ limit: 100 });
    setPhoneInput('');
    setEventInput('');
    setInstanceInput('');
    setStartDate(undefined);
    setEndDate(undefined);
  };

  /** Atualizar dados */
  const handleRefresh = () => {
    refetch();
  };

  /** Alternar expansão de linha */
  const toggleExpand = (id: string) => {
    setExpandedId(prev => prev === id ? null : id);
  };

  return (
    <div className="space-y-6">
      {/* Cabeçalho */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Database className="h-8 w-8 text-primary" />
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Raw Ingest Log</h1>
            <p className="text-muted-foreground">
              Auditoria completa de todos os webhooks recebidos dos providers
            </p>
          </div>
        </div>
        <Button onClick={handleRefresh} disabled={isFetching} variant="outline">
          <RefreshCw className={cn("h-4 w-4 mr-2", isFetching && "animate-spin")} />
          Atualizar
        </Button>
      </div>

      {/* Cards de Resumo */}
      <div className="grid gap-4 md:grid-cols-5">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Total</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{summary.total}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-green-600">Processed</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">{summary.processed}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-yellow-600">Discarded</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-yellow-600">{summary.discarded}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-red-600">Error</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-600">{summary.error}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Pending</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{summary.pending}</div>
          </CardContent>
        </Card>
      </div>

      {/* Filtros */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Search className="h-4 w-4" />
            Filtros
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-3 md:grid-cols-4 lg:grid-cols-7">
            {/* Provider */}
            <Select
              value={filters.provider_slug || 'all'}
              onValueChange={v => setFilters(prev => ({ ...prev, provider_slug: v === 'all' ? undefined : v }))}
            >
              <SelectTrigger>
                <SelectValue placeholder="Provider" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos Providers</SelectItem>
                <SelectItem value="z-api">Z-API</SelectItem>
                <SelectItem value="waha">WAHA</SelectItem>
              </SelectContent>
            </Select>

            {/* Status */}
            <Select
              value={filters.resolution_status || 'all'}
              onValueChange={v => setFilters(prev => ({ ...prev, resolution_status: v === 'all' ? undefined : v }))}
            >
              <SelectTrigger>
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos Status</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="processed">Processed</SelectItem>
                <SelectItem value="discarded">Discarded</SelectItem>
                <SelectItem value="error">Error</SelectItem>
              </SelectContent>
            </Select>

            {/* Telefone */}
            <Input
              placeholder="Telefone..."
              value={phoneInput}
              onChange={e => setPhoneInput(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleApplyFilters()}
            />

            {/* Tipo de Evento */}
            <Input
              placeholder="Tipo de evento..."
              value={eventInput}
              onChange={e => setEventInput(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleApplyFilters()}
            />

            {/* Instance ID */}
            <Input
              placeholder="Instance ID..."
              value={instanceInput}
              onChange={e => setInstanceInput(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleApplyFilters()}
            />

            {/* Data Início */}
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" className={cn("justify-start text-left font-normal", !startDate && "text-muted-foreground")}>
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {startDate ? format(startDate, "dd/MM/yyyy") : "Data início"}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar mode="single" selected={startDate} onSelect={setStartDate} className={cn("p-3 pointer-events-auto")} />
              </PopoverContent>
            </Popover>

            {/* Data Fim */}
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" className={cn("justify-start text-left font-normal", !endDate && "text-muted-foreground")}>
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {endDate ? format(endDate, "dd/MM/yyyy") : "Data fim"}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar mode="single" selected={endDate} onSelect={setEndDate} className={cn("p-3 pointer-events-auto")} />
              </PopoverContent>
            </Popover>
          </div>

          {/* Linha de ações de filtro */}
          <div className="flex items-center gap-3 mt-3">
            {/* Limite */}
            <Select
              value={String(filters.limit || 100)}
              onValueChange={v => setFilters(prev => ({ ...prev, limit: Number(v) }))}
            >
              <SelectTrigger className="w-[120px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="50">50 registros</SelectItem>
                <SelectItem value="100">100 registros</SelectItem>
                <SelectItem value="200">200 registros</SelectItem>
                <SelectItem value="500">500 registros</SelectItem>
              </SelectContent>
            </Select>

            <Button onClick={handleApplyFilters} size="sm">
              <Search className="h-4 w-4 mr-1" />
              Buscar
            </Button>
            <Button onClick={handleClearFilters} variant="ghost" size="sm">
              Limpar
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Tabela de Registros */}
      <Card>
        <CardHeader>
          <CardTitle>Registros de Ingestão</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-2">
              {[...Array(5)].map((_, i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : !records || records.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              Nenhum registro encontrado com os filtros aplicados
            </div>
          ) : (
            <div className="rounded-md border overflow-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[30px]"></TableHead>
                    <TableHead>Recebido</TableHead>
                    <TableHead>Provider</TableHead>
                    <TableHead>Evento</TableHead>
                    <TableHead>Telefone</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Motivo Descarte</TableHead>
                    <TableHead>Duração (ms)</TableHead>
                    <TableHead>Instance ID</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {records.map((record) => (
                    <RawIngestRow
                      key={record.id}
                      record={record}
                      isExpanded={expandedId === record.id}
                      onToggle={() => toggleExpand(record.id)}
                    />
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

/**
 * Componente de linha expandível para exibir detalhes do payload
 */
function RawIngestRow({
  record,
  isExpanded,
  onToggle,
}: {
  record: WebhookRawIngestRecord;
  isExpanded: boolean;
  onToggle: () => void;
}) {
  const statusConfig = statusBadgeVariant[record.resolution_status] || statusBadgeVariant.pending;
  const providerColor = providerColors[record.provider_slug] || 'bg-muted text-muted-foreground';

  return (
    <>
      {/* Linha principal */}
      <TableRow
        className="cursor-pointer hover:bg-muted/50"
        onClick={onToggle}
      >
        <TableCell className="w-[30px] pr-0">
          {isExpanded ? (
            <ChevronDown className="h-4 w-4 text-muted-foreground" />
          ) : (
            <ChevronRight className="h-4 w-4 text-muted-foreground" />
          )}
        </TableCell>
        <TableCell className="whitespace-nowrap text-xs">
          {record.received_at
            ? format(new Date(record.received_at), "dd/MM/yyyy HH:mm:ss")
            : '-'}
        </TableCell>
        <TableCell>
          <Badge variant="outline" className={cn("text-xs", providerColor)}>
            {record.provider_slug}
          </Badge>
        </TableCell>
        <TableCell className="text-xs font-mono max-w-[150px] truncate">
          {record.event_type || '-'}
        </TableCell>
        <TableCell className="text-xs font-mono">
          {record.phone || '-'}
        </TableCell>
        <TableCell>
          <Badge variant="outline" className={cn("text-xs", statusConfig.className)}>
            {statusConfig.label}
          </Badge>
        </TableCell>
        <TableCell className="text-xs max-w-[150px] truncate">
          {record.discard_reason || '-'}
        </TableCell>
        <TableCell className="text-xs text-right">
          {record.processing_duration_ms != null
            ? `${record.processing_duration_ms}ms`
            : '-'}
        </TableCell>
        <TableCell className="text-xs font-mono max-w-[120px] truncate">
          {record.instance_id || '-'}
        </TableCell>
      </TableRow>

      {/* Linha expandida com payload JSON */}
      {isExpanded && (
        <TableRow>
          <TableCell colSpan={9} className="bg-muted/30 p-0">
            <div className="p-4">
              <p className="text-xs font-semibold text-muted-foreground mb-2">Raw Payload:</p>
              <pre className="text-xs bg-background rounded-md p-3 overflow-auto max-h-[400px] border">
                {JSON.stringify(record.raw_payload, null, 2)}
              </pre>
            </div>
          </TableCell>
        </TableRow>
      )}
    </>
  );
}
